/**
 * RelationshipGraph - D3.js Force-Directed Graph for Relationships
 * Visualizes connections between organisations, people, and other entities
 */

import React, { useEffect, useRef, useState } from 'react';
import * as d3 from 'd3';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Slider } from './ui/slider';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { 
  ZoomIn, 
  ZoomOut, 
  Maximize2, 
  RefreshCw, 
  Filter, 
  Download,
  Building2,
  User,
  MapPin,
  Zap,
  Home,
  FileText,
  Link2,
} from 'lucide-react';
import { cn } from '../lib/utils';

// Node type colors
const NODE_COLORS = {
  organisation: '#3b82f6', // blue
  person: '#22c55e', // green
  location: '#f59e0b', // amber
  property: '#f97316', // orange
  event: '#a855f7', // purple
  relationship: '#ec4899', // pink
  document: '#14b8a6', // teal
};

const NODE_ICONS = {
  organisation: Building2,
  person: User,
  location: MapPin,
  property: Home,
  event: Zap,
  document: FileText,
};

export function RelationshipGraph({ 
  records = [], 
  relationships = [],
  onNodeClick,
  width = 800,
  height = 600,
  className,
}) {
  const svgRef = useRef(null);
  const containerRef = useRef(null);
  const simulationRef = useRef(null);
  
  const [selectedNode, setSelectedNode] = useState(null);
  const [filterType, setFilterType] = useState('all');
  const [linkStrength, setLinkStrength] = useState([0.5]);
  const [chargeStrength, setChargeStrength] = useState([-300]);
  
  // Transform records and relationships into graph data
  const graphData = React.useMemo(() => {
    // Create nodes from records
    const nodeMap = new Map();
    
    // Add all records as nodes
    records.forEach(record => {
      nodeMap.set(record.id, {
        id: record.id,
        label: record.title,
        type: record.record_type,
        data: record,
      });
    });
    
    // Create links from relationships
    const links = [];
    relationships.forEach(rel => {
      const content = rel.content || {};
      
      // Try to find source and target nodes
      let sourceId = content.entity_1_id;
      let targetId = content.entity_2_id;
      
      // If no IDs, try to find by name
      if (!sourceId) {
        const sourceNode = records.find(r => r.title === content.entity_1_name);
        sourceId = sourceNode?.id;
      }
      if (!targetId) {
        const targetNode = records.find(r => r.title === content.entity_2_name);
        targetId = targetNode?.id;
      }
      
      // Create synthetic nodes for entities not in records
      if (!nodeMap.has(sourceId) && content.entity_1_name) {
        sourceId = `entity-${content.entity_1_name}`;
        nodeMap.set(sourceId, {
          id: sourceId,
          label: content.entity_1_name,
          type: content.entity_1_type || 'organisation',
          data: { title: content.entity_1_name },
        });
      }
      
      if (!nodeMap.has(targetId) && content.entity_2_name) {
        targetId = `entity-${content.entity_2_name}`;
        nodeMap.set(targetId, {
          id: targetId,
          label: content.entity_2_name,
          type: content.entity_2_type || 'organisation',
          data: { title: content.entity_2_name },
        });
      }
      
      if (sourceId && targetId && nodeMap.has(sourceId) && nodeMap.has(targetId)) {
        links.push({
          source: sourceId,
          target: targetId,
          type: content.relationship_type || 'related_to',
          strength: content.strength || 0.5,
          isCurrent: content.is_current !== false,
          data: rel,
        });
      }
    });
    
    // Filter nodes by type if needed
    let filteredNodes = Array.from(nodeMap.values());
    if (filterType !== 'all') {
      filteredNodes = filteredNodes.filter(n => n.type === filterType);
    }
    const filteredNodeIds = new Set(filteredNodes.map(n => n.id));
    
    // Filter links to only include those with both nodes present
    const filteredLinks = links.filter(l => 
      filteredNodeIds.has(typeof l.source === 'object' ? l.source.id : l.source) &&
      filteredNodeIds.has(typeof l.target === 'object' ? l.target.id : l.target)
    );
    
    return { nodes: filteredNodes, links: filteredLinks };
  }, [records, relationships, filterType]);
  
  // Initialize and update D3 simulation
  useEffect(() => {
    if (!svgRef.current || graphData.nodes.length === 0) return;
    
    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();
    
    const container = svg.append('g').attr('class', 'graph-container');
    
    // Create zoom behavior
    const zoom = d3.zoom()
      .scaleExtent([0.1, 4])
      .on('zoom', (event) => {
        container.attr('transform', event.transform);
      });
    
    svg.call(zoom);
    
    // Create arrow markers for directed links
    svg.append('defs').selectAll('marker')
      .data(['arrow'])
      .enter()
      .append('marker')
      .attr('id', 'arrow')
      .attr('viewBox', '0 -5 10 10')
      .attr('refX', 20)
      .attr('refY', 0)
      .attr('markerWidth', 6)
      .attr('markerHeight', 6)
      .attr('orient', 'auto')
      .append('path')
      .attr('fill', '#666')
      .attr('d', 'M0,-5L10,0L0,5');
    
    // Create simulation
    const simulation = d3.forceSimulation(graphData.nodes)
      .force('link', d3.forceLink(graphData.links)
        .id(d => d.id)
        .distance(100)
        .strength(linkStrength[0]))
      .force('charge', d3.forceManyBody().strength(chargeStrength[0]))
      .force('center', d3.forceCenter(width / 2, height / 2))
      .force('collision', d3.forceCollide().radius(30));
    
    simulationRef.current = simulation;
    
    // Create links
    const link = container.append('g')
      .attr('class', 'links')
      .selectAll('line')
      .data(graphData.links)
      .enter()
      .append('line')
      .attr('stroke', d => d.isCurrent ? '#666' : '#444')
      .attr('stroke-opacity', d => d.isCurrent ? 0.8 : 0.4)
      .attr('stroke-width', d => Math.max(1, d.strength * 3))
      .attr('stroke-dasharray', d => d.isCurrent ? 'none' : '5,5')
      .attr('marker-end', 'url(#arrow)');
    
    // Create link labels
    const linkLabel = container.append('g')
      .attr('class', 'link-labels')
      .selectAll('text')
      .data(graphData.links)
      .enter()
      .append('text')
      .attr('font-size', '8px')
      .attr('fill', '#888')
      .attr('text-anchor', 'middle')
      .text(d => d.type?.replace(/_/g, ' '));
    
    // Create nodes
    const node = container.append('g')
      .attr('class', 'nodes')
      .selectAll('g')
      .data(graphData.nodes)
      .enter()
      .append('g')
      .attr('class', 'node')
      .style('cursor', 'pointer')
      .call(d3.drag()
        .on('start', dragstarted)
        .on('drag', dragged)
        .on('end', dragended));
    
    // Node circles
    node.append('circle')
      .attr('r', 15)
      .attr('fill', d => NODE_COLORS[d.type] || '#666')
      .attr('stroke', d => selectedNode?.id === d.id ? '#fff' : 'transparent')
      .attr('stroke-width', 2)
      .on('click', (event, d) => {
        setSelectedNode(d);
        if (onNodeClick) onNodeClick(d);
      });
    
    // Node icons (as text for simplicity)
    node.append('text')
      .attr('text-anchor', 'middle')
      .attr('dy', '0.35em')
      .attr('fill', '#fff')
      .attr('font-size', '10px')
      .attr('pointer-events', 'none')
      .text(d => d.type?.[0]?.toUpperCase() || '?');
    
    // Node labels
    node.append('text')
      .attr('dy', 28)
      .attr('text-anchor', 'middle')
      .attr('fill', '#ccc')
      .attr('font-size', '10px')
      .attr('pointer-events', 'none')
      .text(d => d.label?.length > 15 ? d.label.slice(0, 15) + '...' : d.label);
    
    // Update positions on tick
    simulation.on('tick', () => {
      link
        .attr('x1', d => d.source.x)
        .attr('y1', d => d.source.y)
        .attr('x2', d => d.target.x)
        .attr('y2', d => d.target.y);
      
      linkLabel
        .attr('x', d => (d.source.x + d.target.x) / 2)
        .attr('y', d => (d.source.y + d.target.y) / 2);
      
      node.attr('transform', d => `translate(${d.x},${d.y})`);
    });
    
    // Drag functions
    function dragstarted(event, d) {
      if (!event.active) simulation.alphaTarget(0.3).restart();
      d.fx = d.x;
      d.fy = d.y;
    }
    
    function dragged(event, d) {
      d.fx = event.x;
      d.fy = event.y;
    }
    
    function dragended(event, d) {
      if (!event.active) simulation.alphaTarget(0);
      d.fx = null;
      d.fy = null;
    }
    
    return () => {
      simulation.stop();
    };
  }, [graphData, width, height, linkStrength, chargeStrength, selectedNode, onNodeClick]);
  
  // Control functions
  const handleZoomIn = () => {
    const svg = d3.select(svgRef.current);
    svg.transition().call(d3.zoom().scaleBy, 1.3);
  };
  
  const handleZoomOut = () => {
    const svg = d3.select(svgRef.current);
    svg.transition().call(d3.zoom().scaleBy, 0.7);
  };
  
  const handleReset = () => {
    const svg = d3.select(svgRef.current);
    svg.transition().call(d3.zoom().transform, d3.zoomIdentity);
    if (simulationRef.current) {
      simulationRef.current.alpha(1).restart();
    }
  };
  
  const handleFitToView = () => {
    const svg = d3.select(svgRef.current);
    svg.transition().call(
      d3.zoom().transform,
      d3.zoomIdentity.translate(width / 4, height / 4).scale(0.5)
    );
  };
  
  if (records.length === 0 && relationships.length === 0) {
    return (
      <div className={cn("flex items-center justify-center bg-bg-secondary rounded-lg", className)} style={{ width, height }}>
        <div className="text-center text-text-tertiary">
          <Link2 className="w-12 h-12 mx-auto mb-2 opacity-30" />
          <p>No data to visualize</p>
          <p className="text-sm">Add records and relationships to see the graph</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className={cn("relative bg-bg-secondary rounded-lg overflow-hidden", className)} ref={containerRef}>
      {/* Controls */}
      <div className="absolute top-4 left-4 z-10 flex flex-col gap-2">
        <div className="flex gap-1 bg-surface-card/90 backdrop-blur rounded-lg p-1">
          <Button variant="ghost" size="icon" onClick={handleZoomIn} title="Zoom In">
            <ZoomIn className="w-4 h-4" />
          </Button>
          <Button variant="ghost" size="icon" onClick={handleZoomOut} title="Zoom Out">
            <ZoomOut className="w-4 h-4" />
          </Button>
          <Button variant="ghost" size="icon" onClick={handleFitToView} title="Fit to View">
            <Maximize2 className="w-4 h-4" />
          </Button>
          <Button variant="ghost" size="icon" onClick={handleReset} title="Reset">
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
        
        <div className="bg-surface-card/90 backdrop-blur rounded-lg p-3 w-48 space-y-3">
          <div>
            <label className="text-xs text-text-tertiary">Filter by Type</label>
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="organisation">Organisations</SelectItem>
                <SelectItem value="person">People</SelectItem>
                <SelectItem value="event">Events</SelectItem>
                <SelectItem value="location">Locations</SelectItem>
                <SelectItem value="property">Properties</SelectItem>
                <SelectItem value="document">Documents</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>
      
      {/* Legend */}
      <div className="absolute top-4 right-4 z-10 bg-surface-card/90 backdrop-blur rounded-lg p-3">
        <div className="text-xs text-text-tertiary mb-2">Legend</div>
        <div className="space-y-1">
          {Object.entries(NODE_COLORS).map(([type, color]) => (
            <div key={type} className="flex items-center gap-2">
              <div 
                className="w-3 h-3 rounded-full" 
                style={{ backgroundColor: color }}
              />
              <span className="text-xs text-text-secondary capitalize">{type}</span>
            </div>
          ))}
        </div>
      </div>
      
      {/* Selected Node Info */}
      {selectedNode && (
        <div className="absolute bottom-4 left-4 z-10 bg-surface-card/90 backdrop-blur rounded-lg p-3 max-w-xs">
          <div className="flex items-center gap-2 mb-2">
            <div 
              className="w-4 h-4 rounded-full" 
              style={{ backgroundColor: NODE_COLORS[selectedNode.type] }}
            />
            <span className="font-medium text-text-primary">{selectedNode.label}</span>
          </div>
          <Badge variant="outline" className="capitalize text-xs">
            {selectedNode.type}
          </Badge>
          {selectedNode.data?.id && (
            <p className="text-xs text-text-tertiary mt-1 font-mono">
              {selectedNode.data.id.slice(0, 8)}...
            </p>
          )}
        </div>
      )}
      
      {/* Stats */}
      <div className="absolute bottom-4 right-4 z-10 text-xs text-text-tertiary">
        {graphData.nodes.length} nodes &middot; {graphData.links.length} links
      </div>
      
      {/* SVG Canvas */}
      <svg 
        ref={svgRef} 
        width={width} 
        height={height}
        className="bg-bg-secondary"
        data-testid="relationship-graph-svg"
      />
    </div>
  );
}

export default RelationshipGraph;
